<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12">
      <div class="page-header d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4">
        <div>
          <h2 class="page-title text-white font-weight-bold mb-1">
            <i class="mdi mdi-hand-heart-outline text-info mr-2"></i>{{ $t('approvals.compensationTitle') }}
          </h2>
          <p class="text-muted mb-0 small">
            {{ $t('approvals.compensationSubtitle') }}
          </p>
        </div>
        <div class="mt-3 mt-sm-0">
          <button class="btn btn-outline-secondary btn-sm" @click="fetchData">
            <i class="mdi mdi-refresh mr-1"></i> {{ $t('common.refresh') || 'Segarkan' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Metric Stat Cards -->
    <div class="col-12 mb-4">
      <div class="row">
        <div class="col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.pendingReview') }}</span>
                <h3 class="text-warning font-weight-bold mb-0 mt-1">{{ countByStatus('pending') }}</h3>
              </div>
              <div class="rounded p-2 text-warning" style="background: rgba(245, 158, 11, 0.15);">
                <i class="mdi mdi-clock-alert-outline" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.approved') }}</span>
                <h3 class="text-success font-weight-bold mb-0 mt-1">{{ countByStatus('approved') }}</h3>
              </div>
              <div class="rounded p-2 text-success" style="background: rgba(16, 185, 129, 0.15);">
                <i class="mdi mdi-check-decagram-outline" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.rejected') }}</span>
                <h3 class="text-danger font-weight-bold mb-0 mt-1">{{ countByStatus('rejected') }}</h3>
              </div>
              <div class="rounded p-2 text-danger" style="background: rgba(239, 68, 68, 0.15);">
                <i class="mdi mdi-close-octagon-outline" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.totalCompensations') }}</span>
                <h3 class="text-white font-weight-bold mb-0 mt-1">{{ approvals.length }} <small class="text-info">{{ $t('approvals.tasks') }}</small></h3>
              </div>
              <div class="rounded p-2 text-info" style="background: rgba(6, 182, 212, 0.15);">
                <i class="mdi mdi-hand-heart" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <div class="col-12 grid-margin">
      <div class="card bg-card border-card">
        <div class="card-body">
          <!-- Toolbar: Status Filters & Search Box -->
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <!-- Filter Pills & Batch Action -->
            <div class="d-flex flex-wrap align-items-center gap-2 mb-2 mb-md-0">
              <div class="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="statusFilter = 'all'"
                >
                  {{ $t('common.all') || 'Semua' }} ({{ approvals.length }})
                </button>
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'pending' ? 'btn-warning' : 'btn-outline-secondary'"
                  @click="statusFilter = 'pending'"
                >
                  {{ $t('common.pending') || 'Menunggu' }} ({{ countByStatus('pending') }})
                </button>
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'approved' ? 'btn-success' : 'btn-outline-secondary'"
                  @click="statusFilter = 'approved'"
                >
                  {{ $t('common.approved') || 'Disetujui' }} ({{ countByStatus('approved') }})
                </button>
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'rejected' ? 'btn-danger' : 'btn-outline-secondary'"
                  @click="statusFilter = 'rejected'"
                >
                  {{ $t('common.rejected') || 'Ditolak' }} ({{ countByStatus('rejected') }})
                </button>
              </div>

              <button
                v-if="countByStatus('pending') > 0"
                type="button"
                class="btn btn-sm btn-outline-success font-weight-bold ml-md-2"
                @click="batchApprove"
                :disabled="loading"
              >
                <i class="mdi mdi-check-all mr-1"></i> {{ $currentLocale === 'id' ? 'Setujui Semua Sekaligus' : 'Quick Approve All' }} ({{ countByStatus('pending') }})
              </button>
            </div>

            <!-- Search Input -->
            <div class="search-box" style="max-width: 320px; width: 100%;">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-input border-card text-muted">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('approvals.searchPlaceholder')"
                />
                <button
                  v-if="search"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="search = ''"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover align-middle" style="width: 100%;">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th style="width: 38px;">No</th>
                  <th style="width: 175px;">{{ $t('approvals.employee') }}</th>
                  <th style="width: 205px;">{{ $t('quests.compensationTask') }}</th>
                  <th style="width: 125px;">{{ $currentLocale === 'id' ? 'Bukti / Lampiran' : 'Proof / Attachment' }}</th>
                  <th style="width: 105px;">{{ $t('approvals.submittedAt') }}</th>
                  <th style="width: 105px;">{{ $t('approvals.verificationStatus') }}</th>
                  <th class="text-right" style="width: 115px; padding-right: 0.75rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in paginatedApproval" :key="data.id">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-circle mr-2 d-flex align-items-center justify-content-center text-white font-weight-bold flex-shrink-0"
                        style="width: 36px; height: 36px; background: rgba(6, 182, 212, 0.2); border: 1px solid rgba(6, 182, 212, 0.4);"
                      >
                        {{ (data.user?.name || 'P')[0].toUpperCase() }}
                      </div>
                      <div class="overflow-hidden">
                        <div class="text-white font-weight-bold text-truncate" :title="data.user?.name">{{ data.user?.name || ($currentLocale === 'id' ? 'Karyawan' : 'Employee') }}</div>
                        <small class="text-muted text-truncate d-block" :title="data.user?.email">{{ data.user?.email || '-' }}</small>
                      </div>
                    </div>
                  </td>
                  <td class="overflow-hidden">
                    <div class="text-white font-weight-medium text-truncate" :title="data.compensation?.name">
                      <i class="mdi mdi-hand-heart text-info mr-1"></i>
                      {{ data.compensation?.name || ($currentLocale === 'id' ? 'Tugas Kompensasi' : 'Compensation Task') }}
                    </div>
                    <small class="text-muted text-truncate d-block" v-if="data.wrong_doing || data.compensation?.description">
                      {{ data.wrong_doing || data.compensation?.description }}
                    </small>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <button
                        v-if="data.evidence_path || data.evidence"
                        type="button"
                        @click="viewProof(data)"
                        class="btn btn-xs btn-outline-info mr-1"
                        :title="$currentLocale === 'id' ? 'Lihat Foto Bukti' : 'View Photo Proof'"
                      >
                        <i class="mdi mdi-image-area mr-1"></i> {{ $currentLocale === 'id' ? 'Foto' : 'Photo' }}
                      </button>
                      <a
                        v-if="data.external_link"
                        :href="data.external_link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="btn btn-xs btn-outline-primary mr-1"
                        :title="$currentLocale === 'id' ? 'Buka Link Eksternal' : 'Open External Link'"
                      >
                        <i class="mdi mdi-link-variant mr-1"></i> {{ $currentLocale === 'id' ? 'Tautan' : 'Link' }}
                      </a>
                      <span
                        v-if="data.comment"
                        class="badge badge-dark border border-secondary text-muted"
                        :title="data.comment"
                      >
                        <i class="mdi mdi-comment-text-outline"></i>
                      </span>
                      <span v-if="!data.evidence_path && !data.evidence && !data.external_link && !data.comment" class="text-muted small">
                        -
                      </span>
                    </div>
                  </td>
                  <td class="text-muted small">
                    {{ formatDate(data.created_at) }}
                  </td>
                  <td>
                    <span
                      class="badge font-weight-bold"
                      :class="{
                        'badge-warning': (data.status || 'pending').toLowerCase() === 'pending',
                        'badge-success': (data.status || '').toLowerCase() === 'approved',
                        'badge-danger': (data.status || '').toLowerCase() === 'rejected'
                      }"
                    >
                      <i
                        class="mdi mr-1"
                        :class="{
                          'mdi-clock-outline': (data.status || 'pending').toLowerCase() === 'pending',
                          'mdi-check-circle': (data.status || '').toLowerCase() === 'approved',
                          'mdi-close-circle': (data.status || '').toLowerCase() === 'rejected'
                        }"
                      ></i>
                      {{ $currentLocale === 'id' ? ({ pending: 'MENUNGGU', approved: 'DISETUJUI', rejected: 'DITOLAK' }[(data.status || 'pending').toLowerCase()] || (data.status || 'pending').toUpperCase()) : (data.status || 'pending').toUpperCase() }}
                    </span>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <template v-if="(data.status || 'pending').toLowerCase() === 'pending'">
                        <button
                          type="button"
                          class="btn btn-xs btn-success font-weight-bold mr-1"
                          @click="quickApprove(data)"
                          :disabled="actionLoading === data.id"
                          :title="$currentLocale === 'id' ? 'Setujui Langsung' : 'Quick Approve'"
                        >
                          <i class="mdi mdi-check"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-xs btn-danger font-weight-bold mr-1"
                          @click="quickReject(data)"
                          :disabled="actionLoading === data.id"
                          :title="$currentLocale === 'id' ? 'Tolak dengan Alasan' : 'Reject with Note'"
                        >
                          <i class="mdi mdi-close"></i>
                        </button>
                      </template>
                      <router-link
                        :to="{ name: 'EditCompensationApproval', params: { id: String(data.id) } }"
                        class="btn btn-xs btn-outline-secondary"
                        :title="$t('approvals.reviewCompensation')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedApproval.length === 0">
                  <td colspan="7" class="text-center py-5">
                    <EmptyState
                      icon="mdi-hand-heart-outline"
                      :title="$t('approvals.emptyCompensationTitle')"
                      :description="$t('approvals.emptyCompensationDesc')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredApproval.length > itemsPerPage" class="mt-4">
            <PaginationComponents
              :totalItems="filteredApproval.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCompensationApproval, updateCompensationApproval } from "../../services/approvalService";
import moment from "moment";
import PaginationComponents from "../../components/PaginationComponents.vue";
import EmptyState from "../../components/EmptyState.vue";
import config from "../../config/config";
import Swal from "sweetalert2";

export default {
  name: "CompensationApproval",
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      approvals: [],
      currentPage: 1,
      itemsPerPage: 10,
      search: "",
      statusFilter: "all",
      loading: false,
      actionLoading: null,
    };
  },
  computed: {
    countByStatus() {
      return (status) => {
        if (!this.approvals) return 0;
        return this.approvals.filter(
          (item) => (item.status || "pending").toLowerCase() === status.toLowerCase()
        ).length;
      };
    },
    filteredApproval() {
      let result = this.approvals || [];
      if (this.statusFilter !== "all") {
        result = result.filter(
          (item) => (item.status || "pending").toLowerCase() === this.statusFilter.toLowerCase()
        );
      }
      if (this.search) {
        const query = this.search.toLowerCase();
        result = result.filter((item) => {
          const userName = (item.user?.name || "").toLowerCase();
          const ruleName = (item.compensation?.name || "").toLowerCase();
          return userName.includes(query) || ruleName.includes(query);
        });
      }
      return result;
    },
    paginatedApproval() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredApproval.slice(start, start + this.itemsPerPage);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD MMM YYYY, HH:mm");
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await getCompensationApproval();
        this.approvals = response.data || [];
      } catch (error) {
        console.error("Error fetching compensation approvals:", error);
        this.approvals = [];
      } finally {
        this.loading = false;
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
    getEvidenceUrl(item) {
      const ev = item.evidence_path || item.evidence;
      if (!ev) return null;
      if (ev.startsWith("http")) return ev;
      return `${config.STORAGE_URL}${ev}`;
    },
    viewProof(item) {
      const url = this.getEvidenceUrl(item);
      if (!url) return;
      const isId = this.$currentLocale === "id";
      Swal.fire({
        title: item.compensation?.name || (isId ? "Pratinjau Bukti Klaim" : "Proof Preview"),
        html: `
          <div class="text-left mb-2 small text-muted">${isId ? "Dikirim oleh" : "Submitted by"}: <strong class="text-white">${item.user?.name || "Player"}</strong></div>
          ${item.comment ? `<div class="p-2 rounded text-left small mb-3" style="background: #1e293b; color: #cbd5e1; border: 1px solid #334155;"><em>"${item.comment}"</em></div>` : ""}
          <div style="max-height: 480px; overflow: auto; text-align: center;">
            <img src="${url}" class="img-fluid rounded border border-secondary" style="max-height: 450px;" alt="Proof"/>
          </div>
          <div class="mt-3 text-center">
            <a href="${url}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-info">
              <i class="mdi mdi-open-in-new mr-1"></i> ${isId ? "Buka Ukuran Penuh" : "Open Full Size"}
            </a>
          </div>
        `,
        showCloseButton: true,
        showConfirmButton: false,
        background: "#141724",
        customClass: {
          popup: "border border-secondary",
        },
      });
    },
    async quickApprove(item) {
      const isId = this.$currentLocale === "id";
      const compName = item.compensation?.name || (isId ? "tugas kompensasi ini" : "this compensation task");
      const playerName = item.user?.name || "player";

      const result = await Swal.fire({
        title: isId ? "Setujui Tugas Kompensasi?" : "Approve Compensation Task?",
        html: isId
          ? `Konfirmasi persetujuan penebusan sanksi untuk <b>${playerName}</b> pada <b>${compName}</b>.`
          : `Confirm approval of penalty recovery for <b>${playerName}</b> on <b>${compName}</b>.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: `<i class="mdi mdi-check mr-1"></i> ${isId ? "Ya, Setujui" : "Yes, Approve"}`,
        cancelButtonText: this.$t("common.cancel"),
        background: "#141724",
      });

      if (result.isConfirmed) {
        try {
          this.actionLoading = item.id;
          await updateCompensationApproval(item.id, "approved", "");
          item.status = "approved";
          Swal.fire({
            icon: "success",
            title: isId ? "Berhasil Disetujui" : "Approved",
            text: isId ? `Tugas kompensasi ${playerName} telah disetujui.` : `Compensation task for ${playerName} has been approved.`,
            timer: 1800,
            showConfirmButton: false,
            background: "#141724",
          });
        } catch (err) {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: this.$t("common.error"),
            text: err.response?.data?.message || (isId ? "Gagal memproses persetujuan." : "Failed to process approval."),
            background: "#141724",
          });
        } finally {
          this.actionLoading = null;
        }
      }
    },
    async quickReject(item) {
      const isId = this.$currentLocale === "id";
      const playerName = item.user?.name || "player";

      const { value: reason } = await Swal.fire({
        title: isId ? "Tolak Tugas Kompensasi?" : "Reject Compensation Task?",
        html: isId ? `Masukkan alasan penolakan untuk <b>${playerName}</b>:` : `Enter rejection reason for <b>${playerName}</b>:`,
        input: "textarea",
        inputPlaceholder: isId ? "Contoh: Penebusan sanksi belum dilaksanakan atau bukti sharing belum lengkap..." : "e.g., Recovery task not completed or proof is incomplete...",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: `<i class="mdi mdi-close mr-1"></i> ${isId ? "Tolak Tugas" : "Reject Task"}`,
        cancelButtonText: this.$t("common.cancel"),
        background: "#141724",
        inputValidator: (value) => {
          if (!value) {
            return isId ? "Alasan penolakan wajib diisi!" : "Rejection reason is required!";
          }
        },
      });

      if (reason) {
        try {
          this.actionLoading = item.id;
          await updateCompensationApproval(item.id, "rejected", reason);
          item.status = "rejected";
          item.rejection_notes = reason;
          Swal.fire({
            icon: "success",
            title: isId ? "Tugas Ditolak" : "Task Rejected",
            text: isId ? "Catatan penolakan telah diteruskan ke player." : "Rejection notes have been sent to the player.",
            timer: 1800,
            showConfirmButton: false,
            background: "#141724",
          });
        } catch (err) {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: this.$t("common.error"),
            text: err.response?.data?.message || (isId ? "Gagal memproses penolakan." : "Failed to process rejection."),
            background: "#141724",
          });
        } finally {
          this.actionLoading = null;
        }
      }
    },
    async batchApprove() {
      const isId = this.$currentLocale === "id";
      const pendingItems = this.approvals.filter(
        (i) => (i.status || "pending").toLowerCase() === "pending"
      );
      if (pendingItems.length === 0) return;

      const result = await Swal.fire({
        title: isId ? `Setujui ${pendingItems.length} Kompensasi?` : `Approve ${pendingItems.length} Compensations?`,
        text: isId
          ? `Semua ${pendingItems.length} tugas kompensasi yang menunggu akan disetujui sekaligus.`
          : `All ${pendingItems.length} pending compensation tasks will be approved at once.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: isId ? `Ya, Setujui Semua (${pendingItems.length})` : `Yes, Approve All (${pendingItems.length})`,
        cancelButtonText: this.$t("common.cancel"),
        background: "#141724",
      });

      if (result.isConfirmed) {
        this.loading = true;
        try {
          for (const item of pendingItems) {
            await updateCompensationApproval(item.id, "approved", "");
            item.status = "approved";
          }
          Swal.fire({
            icon: "success",
            title: isId ? "Batch Berhasil" : "Batch Approved",
            text: isId ? `${pendingItems.length} kompensasi telah disetujui sekaligus!` : `${pendingItems.length} compensations approved!`,
            timer: 2000,
            showConfirmButton: false,
            background: "#141724",
          });
        } catch (err) {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: this.$t("common.error"),
            text: isId ? "Terjadi kendala saat memproses beberapa kompensasi." : "An error occurred while processing some compensations.",
            background: "#141724",
          });
        } finally {
          this.loading = false;
        }
      }
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
